(ns practitest.frontend.forms3.entity-form
  (:require [ajax.core :as ajax]
            [clojure.string :as str]
            [goog.functions :as gfuncs]
            [practitest.frontend.common-entities :as common-entities]
            [practitest.frontend.common-entities.events :as common-events]
            [practitest.frontend.common-entities.metadata :as metadata]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [get-translation] :as i18n]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common.permissions :as permissions]
            [practitest.frontend.common.re-frame :refer [partial-dispatch
                                                         partial-subscribe
                                                         conditional-dispatch]]
            [practitest.frontend.components.attachments3 :as attachments]
            [practitest.frontend.components.card-view]
            [practitest.frontend.components.forms :as forms-data]
            [practitest.frontend.components.grid.localstorage :as grid-storage]
            [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
            [practitest.frontend.components.grid.subs :as grid.subs]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.markdown :as markdown]
            [practitest.frontend.components.modal :as modal]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.users :as users]
            [practitest.frontend.components.overlay2 :as overlay2]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.grid :as grid2]
            [practitest.frontend.components2.dropdown :as dropdown]
            [practitest.frontend.components2.tabs :as tabs]
            [practitest.frontend.components2.value-score :as value-score]
            [practitest.frontend.events :as events]
            [practitest.frontend.forms3.entity-form.attachments :refer [attachments-tab]]
            [practitest.frontend.forms3.entity-form.comments :refer [comments-tab]]
            [practitest.frontend.forms3.entity-form.fields :as fields]
            [practitest.frontend.forms3.entity-form.footer :refer [footer]]
            [practitest.frontend.forms3.entity-form.forms :as forms]
            [practitest.frontend.forms3.entity-form.header :as header]
            [practitest.frontend.forms3.link-to-external-modal.main :as link-to-external]
            [practitest.frontend.forms3.instance.step-parameters :as step-parameters]
            [practitest.frontend.forms3.instance.version-selector :as instance-version-section]
            [practitest.frontend.issue.bugtracker :as issue-bugtracker]
            [practitest.frontend.similar-items :as similar-items]
            [practitest.frontend.requirements.testing-coverage :refer [testing-status-panel]]
            [practitest.frontend.tests.test-versioning :as test-versioning]
            [practitest.frontend.forms3.entity-form.collab-events :as collab-events]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.routing.router :as router]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.components2.grid.common :as gc]
            [practitest.frontend.pt-ts-bridge :refer [ConflictItem]]
            [re-frame.core :as re-frame]
            [practitest.frontend.common.keyboard-shortcuts :as keyboard-shortcuts]
            [reagent.core :as reagent]))

(def status-fields #{:issue_status :status_resolution})

(defn conflict-resolution-modal
  [{:keys [conflicts on-cancel form-id entity-type entity-id]}]
  (let [selected-values (reagent/atom {})
        fields @(re-frame/subscribe [::subs/fields])]
    (fn []
      [modal/bootstrap-modal
       {:is-open? true
        :on-request-close on-cancel
        :close-button? true
        :centered? true
        :class "conflicts-modal"}
       [:<>
        [icons/mux-warning]
        (get-translation "entity.conflicts-modal.title" "Resolve Conflicts")]
       [:div
        (doall
          (for [[field-id {:keys [their-value your-value]}] conflicts]
            (let [field (field-id fields)
                  field-column (grid.subs/field->column nil field)]
              [:> ConflictItem
               {:field field
                :key field-id
                :yourValue your-value
                :theirValue their-value
                :TheirValueElement (reagent/as-element [gc/body-fn field-column {field-id their-value} {}])
                :YourValueElement (reagent/as-element [gc/body-fn field-column {field-id your-value} {}])
                :onSelect #(swap! selected-values assoc field-id (js->clj % :keywordize-keys true))}])))]
       ;; Footer
       (let [amount-of-conflicts (count conflicts)]
         [:<>
          [:span.unresolved-conflicts
           [:div.red-indicator] (str amount-of-conflicts " unresolved conflicts")]
          [:div.m-button-group
           [buttons/button
            {:text (get-translation "generic.button.cancel" "Cancel")
             :on-click on-cancel}]
           [buttons/button
            {:primary? true
             :disabled? (not= amount-of-conflicts (count @selected-values))
             :text (get-translation "generic.button.save" "Save")
             :on-click (fn []
                         (let [overwritten (filter (fn [[field-id selected]]
                                                     (= (:your-value (get conflicts field-id)) selected))
                                                   @selected-values)]
                           (when (seq overwritten)
                             (mixpanel/track "Collaborative Editing"
                                             {"Action" "Override Values Occur"
                                              "Entity Type" (name entity-type)
                                              "Entity ID" entity-id
                                              "Fields Overwritten" (map first overwritten)})))
                         (doseq [[field-id field-state] @selected-values]
                           (re-frame/dispatch [::forms/set-field-state form-id field-id {:value field-state
                                                                                         :dirty? true
                                                                                         :touched? false
                                                                                         :errors []}]))
                         (re-frame/dispatch [::collab-events/set-form-touched form-id false])
                         (re-frame/dispatch [:close-conflict-modal]))}]]])])))

(defn panel-has-errors? [form-errors panel-fields]
  (let [error-fields (set (keys form-errors))]
    (some #(contains? error-fields (keyword (:field %))) panel-fields)))

(re-frame/reg-event-db
  ::set-notification-state
  (fn [db [_ form-id state]]
    (assoc-in db [:form-states form-id :status] state)))

(re-frame/reg-event-db
  :open-conflict-modal
  (fn [db [_ form-id conflicts]]
    (assoc db :conflict-modal {:form-id form-id :conflicts conflicts})))

(re-frame/reg-event-db
  :close-conflict-modal
  (fn [db _]
    (dissoc db :conflict-modal)))

(re-frame/reg-event-fx
  ::got-entity
  (fn [{db :db} [_
                 {:keys [form-id
                         entity-type
                         got-entity-data]
                  :as fetch-options}
                 response]]
    (let [entity (:entity response)
          touched? (get-in db [:form-states2 form-id :touched?])
          dirty-fields (->> (get-in db [:form-states2 form-id :fields])
                            (filter (fn [[_ {:keys [dirty?]}]] dirty?))
                            (into {}))
          conflicts (reduce (fn [acc [field-id {:keys [value]}]]
                              (let [response-value (get entity field-id)
                                    old-current-value (get-in db [:entity entity-type :current field-id])
                                    value-changed? (not= value response-value)
                                    changed-by-collab? (not= old-current-value response-value)
                                    is-conflict? (and value-changed? changed-by-collab?)]
                                (if is-conflict?
                                  (assoc acc field-id {:their-value response-value
                                                       :your-value value})
                                  acc)))
                            {}
                            dirty-fields)
          dirty-fields-key-values (into {} (map (fn [[k v]] [k (:value v)]) dirty-fields))
          entity-with-dirty-values (merge entity dirty-fields-key-values)]
      (if (and touched? (seq conflicts))
        ;; Handle conflicts: store them in the DB and let the user resolve
        {:db (-> db
                 (assoc-in [:form-states2 form-id :conflicts] conflicts)
                 (forms/reconcile-form-state form-id false entity-type entity-with-dirty-values))
         :fx [[:dispatch-n [[:open-conflict-modal form-id conflicts]
                            (mixpanel/track "Collaborative Editing"
                                            {"Action" "Conflict Occur"
                                             "Entity Type" (name entity-type)
                                             "Entity ID" (:id entity)
                                             "Conflicts Count" (count conflicts)})]]]}

        ;; No conflicts, proceed with merging response changes
        (let [page-title (str (metadata/get-entity-name entity-type :singular-name)
                              " #" (:display_id entity) " - " (:name entity))
              new? false]
          (dom/update-page-title page-title)
          (let [got-entity-data (if (and (seq got-entity-data) (keyword? (first got-entity-data)))
                                  [got-entity-data]
                                  got-entity-data)]
            (reduce (fn [acc additional-event]
                      (conditional-dispatch acc additional-event fetch-options response))
                    {:db (-> db
                             (update :progress disj ::fetch-entity)
                             (assoc-in [:entity entity-type :current] (assoc entity :type entity-type))
                             (assoc-in [:entity entity-type :traceability] (:traceability response))
                             (cond-> (:instances_limit response)
                               (assoc-in [:entity entity-type :instances_limit] (:instances_limit response)))
                             (update-in [:form-states2 form-id] #(dissoc % :touched?))

                             ;; Apply form state immediately after entity is received
                             (forms/reconcile-form-state form-id new? entity-type entity-with-dirty-values))

                     :fx [[:dispatch [::set-notification-state
                                      form-id
                                      {:status "success"
                                       :type :loaded
                                       ;; TODO: updated_by ?
                                       :updated-at (:updated_at entity)}]]]}
                    got-entity-data)))))))

;; TODO: move to separate namespace ?
;; TODO: similar methods to fetch comments/attachments etc (endpoints via entity metadata)
(re-frame/reg-event-fx
 ::fetch-entity
 (fn [{db :db} [_ {:keys [entity-type
                          project-id
                          entity-id
                          route-params]
                   :as fetch-options}]]
   {:db (update db :progress conj ::fetch-entity)
    :http-xhrio
    {:method :get
     :uri (broutes/get-url (metadata/get-fetch-entity-route entity-type)
                           (merge {:p_id project-id
                                   :id entity-id}
                                  route-params))
     :response-format (ajax/json-response-format {:keywords? true})
     :on-success [::got-entity fetch-options]
     :on-failure [::fetch-entity-failure fetch-options]}}))

(re-frame/reg-event-fx
  ::got-fields
  (fn [{db :db} [_
                 {:keys [form-id
                         new?
                         entity-type]
                  :as fetch-options}
                 fields]]
    ;; Reuse existing logic for fields processing
    (let [new-db (events/got-fields db [::got-fields fields])
          entity (get-in db [:entity entity-type :current])]
      {:db (cond-> (assoc-in new-db [:form-states2 form-id :fields-loading?] false)

                   ;; Apply form state processing as soon as fields are available (only if entity is already loaded)
                   ;; or if that's a new form - we need to handle default values in that case
                   ;; TODO: check that entity ID is correct in the DB
             (or new? entity)
             (forms/reconcile-form-state form-id new? entity-type entity))})))

(re-frame/reg-event-fx
 ::fetch-entity-failure
 (fn [_ [_ {:keys [entity-type]} error]]
   (if (= (:status error) 404)
     (let [index-route (get-in metadata/entities-metadata [entity-type :routes :index])]
       {:dispatch-n [[::notifications/error2 (get-translation "entity.form.entity-not-found" "This entity no longer exists.")]
                     [::router/replace-state index-route]]})
     {:dispatch [::notifications/error2 (get-translation "entity.form.fetch-entity-error-generic" "Failed to load entity. Please try again later.")]})))
;; TODO: move to separate namespace ?
(re-frame/reg-event-fx
  ::fetch-fields
  (fn [{db :db} [_ {:keys [project-id form-id]
                    :as fetch-options}]]
    {:db (assoc-in db [:form-states2 form-id :fields-loading?] true)
     :http-xhrio
     {:method :get
      :uri (broutes/get-url "form_entities#available_fields" {:p_id project-id})
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success [::got-fields fetch-options]
      :on-failure [::events/api-error ::fetch-fields {}]}}))

(re-frame/reg-sub
  ::fields-loading?
  (fn [db [_ form-id]]
    (get-in db [:form-states2 form-id :fields-loading?] false)))

(defn fields-view [form-props section-fields]
  (into [:<>]
        (map (fn [{:keys [field permission]}]
               (let [external-entity? (get-in form-props [:base-entity :external_id])
                     edit? (:edit? form-props)
                     edit-permission  (cond
                                        (and (not edit?) (contains? status-fields field)) false
                                        (and external-entity?
                                             (some #(= field %) #{:description :external_status :issue_status
                                                                  :milestone_status :milestone_start_date
                                                                  :milestone_end_date :milestone_type})) false
                                        (= field :test_type) false
                                        (= field :value_score) false
                                        permission (permissions/has-permission? permission)
                                        :else (:edit-permissions? form-props))]
                 ;; TODO: build-field cleanup !!
                 [fields/build-field
                  {:field-name field
                   :edit-permissions? edit-permission}
                  form-props])))
        section-fields))

(defn inner-tabs-panel
  [{:keys [general-form-inner-tabs edit? entity-type main-state default-active-sub-tab form-id] :as form-props}]
  (let [sub-tabs (remove nil?
                         [(when (contains? general-form-inner-tabs :attachments)
                            (attachments-tab edit? entity-type))
                          (when (contains? general-form-inner-tabs :comments)
                            (comments-tab entity-type))
                          (when (contains? general-form-inner-tabs :similar-items)
                            (similar-items/similar-items-tab {:entity-type entity-type :form-id form-id}))])
        stored-tab (grid-storage/load-entity-form-active-tab)
        tab-found-in-form? (when stored-tab
                             (boolean (some #(= (keyword stored-tab) (:tab-id %)) sub-tabs)))

        active-tab @(re-frame/subscribe [::events/active-sub-tab (or stored-tab default-active-sub-tab)])
        toggle-subtab (fn [tab-id]
                        (let [prev-tab-id (:active-sub-tab @main-state)]
                          (re-frame/dispatch [::events/set-active-sub-tab tab-id])
                          (when (contains? general-form-inner-tabs :similar-items)
                            (cond
                              (= tab-id :similar-items)
                              (do
                                (mixpanel/track (str "AI Similar " (get-in metadata/entities-metadata [entity-type :plural-name :default]))
                                                {"Action" "Click Tab"})
                                (re-frame/dispatch [::similar-items/similar-items-expand entity-type form-id]))

                              (= prev-tab-id :similar-items)
                              (re-frame/dispatch [::similar-items/similar-items-collapse entity-type form-id])))))]
    (when (and stored-tab tab-found-in-form?)
      (re-frame/dispatch [::events/set-active-sub-tab stored-tab]))

    (when (not-empty sub-tabs)
      [:<>
       (into
         [tabs/tabs-container {:on-toggle toggle-subtab
                               :shortcuts? true
                               :active-tab-id (or (and tab-found-in-form? active-tab) default-active-sub-tab)}]
         (for [tab sub-tabs]
           [tabs/tab {:tab-id (:tab-id tab) :icon (when (= (:tab-id tab) :similar-items) [icons/mux-similar-items]) :custom-class (when  (contains? general-form-inner-tabs :similar-items)  "similar-items-tab")}
            (:tab-title tab)]))
       [:div.sub-tab-content
        [keyboard-shortcuts/add-shortcut {:shortcut [:entity-form :add-attachment]
                                          :handler (fn [] (toggle-subtab :attachments))}]
        [keyboard-shortcuts/add-shortcut {:shortcut [:entity-form :add-comments]
                                          :handler (fn [] (toggle-subtab :comments))}]
        (let [active-tab-data (some #(when (= active-tab (:tab-id %)) %) sub-tabs)]
          (if active-tab-data
            [(:tab-view active-tab-data) form-props]
            ;; If active tab doesn't exist, use the first available tab
            (when (seq sub-tabs)
              (let [first-tab (first sub-tabs)]
                ;; Update the active tab in the state
                (swap! main-state assoc :active-sub-tab (:tab-id first-tab))
                [(:tab-view first-tab) form-props]))))]])))

(defn clean-section-fields-from-integration-fields
  [{:keys [layout entity-type additional-fields] :as form-props} section-key]
  (let [bugtracker-fields @(re-frame/subscribe [::issue-bugtracker/bugtracker-fields])
        entity-bugtracker-fields (issue-bugtracker/filter-entity-bugtracker-fields bugtracker-fields entity-type)
        bugtracker-field-names (set (map :name entity-bugtracker-fields))
        section-fields (get-in layout [section-key :fields])]
    (if (some? additional-fields)
      (remove #(contains? bugtracker-field-names (:field %)) section-fields)
      section-fields)))

(defn configure-fields-menu [entity]
  (let [configure-expanded? (reagent/atom false)
        entity-name (metadata/get-entity-name entity :singular-name)
        hide-configure! (fn []
                          (reset! configure-expanded? false))
        toggle-configure! (fn []
                            (mixpanel/track (str entity-name " Form Actions")
                                            {"Action" "Configure Clicked"})
                            (swap! configure-expanded? not))

        new-field-url (broutes/get-url-view "custom_fields#new")
        edit-form-url (broutes/get-url-view "sections#index"
                                            {:project_id @(re-frame/subscribe [::subs/current-project-id])})
        entity-name (metadata/get-entity-name entity :singular-name)
        module-lower (str/lower-case  (get-in metadata/entities-metadata [entity :module]))
        can-customize-custom-fields? (permissions/has-permission? "customization")]
    (fn []
      [overlay2/floating-ui-overlay
       {:on-request-close hide-configure!
        :show? @configure-expanded?
        :offset 0
        :placement "bottom-end"}
       [buttons/button
        {:tertiary? true
         :on-click toggle-configure!
         :active? @configure-expanded?
         :class "configure-fields-button"
         :text (get-translation "generic.button.configure"
                                "Configure")
         :icon [icons/mux-configure-settings]
         :end-icon [icons/mux-chevron-down2]}]
       [dropdown/dropdown-menu-body {:offset 0
                                     :placement "bottom"}
        [dropdown/dropdown-menu-item
         {:icon [icons/mux-plus-circle]
          :href new-field-url
          :text (get-translation "entity.form.add-new-field"
                                 "Add New Field")
          :mixpanel ["Form Actions" {"Action" (str "Add New Field " entity-name)}]
          :disabled?  (not can-customize-custom-fields?)}]
        [dropdown/dropdown-menu-item
         {:icon [icons/mux-project-settings]
          :href (str edit-form-url "?entity=" module-lower)
          :text (get-translation "entity.form.edit-form-layout"
                                 "Edit Form Layout")
          :mixpanel ["Form Actions" {"Action" (str "Edit Form Layout " entity-name)}]
          :disabled?  (not can-customize-custom-fields?)}]]])))

(defn filter-entity-fields
  [entity-fields to-remove?]
  (into {}
        (filter (fn [[k _]] (not (to-remove? k))) entity-fields)))

(defn sort-fields-by-entity-fields
  [fields entity-fields]
  (->> fields
       (map (fn [f]
              (let [entity-field (get entity-fields (keyword f))]
                (merge {:field (keyword f)}
                       entity-field))))
       (sort-by (juxt :position :order))))

(defn sort-fields
  [fields]
  (->> fields
       (map (fn [[field field-map]]
              (merge {:field field} field-map)))
       (sort-by (juxt :position :order))))

(def sections-keys
  {:title :title
   :automation :automation
   :description "0-0"
   :linked-runs "0-1"
   :testing-status "1-0"
   :test-versioning "1-0"
   :step-parameters "1-0"
   :general-info "1-1"})

(defn update-layout
  ([layout entity-sections entity-fields]
   (reduce
     (fn [acc section]
       (let [section-id (:id section)
             system-section (:system_section section)
             label (:label section)
             section-key (str (:column_position section) "-" (:row_position section))
             fields (filter (fn [field]
                              (let [field-section-id (:section_id (val field))]
                                (or (= field-section-id section-id)
                                    (and (= section-key (:general-info sections-keys))
                                         (nil? field-section-id)))))
                            entity-fields)
             sorted-fields (sort-fields fields)
             formatted-fields (map #(select-keys % [:field]) sorted-fields)]
         (update acc section-key
                 (fn [existing]
                   (let [current-map (or existing {:fields []})
                         updated-fields (into (:fields current-map) formatted-fields)]
                     (assoc current-map
                            :fields updated-fields
                            :label label
                            :system-section system-section
                            :section-id section-id))))))
     layout
     entity-sections))

  ; manual layout changes/additions
  ([layout side fields system-section entity-fields]
   (update layout side
           (fn [current]
             (let [current-map (or current {:fields []})
                   sorted-fields (sort-fields-by-entity-fields fields entity-fields)
                   updated-fields (into (:fields current-map)
                                        (map #(select-keys % [:field]) sorted-fields))]
               (assoc current-map :fields updated-fields
                      :system-section system-section))))))

(defn top-left-side-section [form-props section-key]
  (let [panel-ref (reagent/atom nil)]
    (reagent/create-class
      {:reagent-render
       (fn [{:keys [layout] :as form-props}]
         (let [section-fields (get-in layout [section-key :fields])]
           [:div
            {:ref #(reset! panel-ref %)}
            [fields-view form-props section-fields]]))})))

(defn side-sections [form-props section-key]
  (let [panel-ref (reagent/atom nil)
        show-more? (reagent/atom false)
        user-expanded? (reagent/atom false)
        section-collapsed? (reagent/atom nil)]
    (reagent/create-class
      {:component-did-mount
       (fn [_]
         (when-let [container @panel-ref]
           (let [container-height (.-clientHeight container)
                 content-height (.-scrollHeight container)]
             (reset! show-more? (> content-height container-height)))))
       :reagent-render
       (fn [{:keys [layout get-form-state base-entity value-score? new? entity-type entity-id] :as form-props}]
         (let [form-state @(get-form-state)
               form-errors (forms-data/collect-form-errors form-state)
               section-label (if (= (get-in layout [section-key :system-section]) "general_info")
                               (get-translation "entity.form.general-info" "General Info")
                               (get-in layout [section-key :label]))
               system-section (get-in layout [section-key :system-section])
               section-fields (->> (clean-section-fields-from-integration-fields form-props section-key)
                                   (remove (fn [{:keys [field] :as section-field}]
                                             (or
                                               ;; Always remove requirement_status field
                                               (= :requirement_status field)
                                               (= :version_label field)
                                               ;; If Value score UI is enabled - remove value score field from section
                                               (and value-score? (= :value_score field))))))
               has-errors? (panel-has-errors? form-errors section-fields)
               ;; Initialize collapsed state from localStorage on first render
               _ (when (nil? @section-collapsed?)
                   (reset! section-collapsed?
                           (grid-storage/get-section-collapsed entity-type section-key false)))
               collapsed? @section-collapsed?
               expanded? (or @user-expanded?
                             has-errors?)]

           [:<>
            (when (and value-score?
                       (= section-key (:general-info sections-keys)))
              [:div.side-panel-section
               [value-score/value-score-component entity-type entity-id (:value_score_data base-entity)]])

            [:div.side-panel-section
             [:div.fields-panel.general-fields-panel
              {:ref   #(reset! panel-ref %)
               :class (dom/classes-pairs-v :expanded expanded?
                                           :section-collapsed collapsed?)}
              [:div.header
               {:class (dom/classes-pairs-v :collapsed collapsed?)}
               [buttons/icon-button
                {:icon (if collapsed?
                         [icons/mux-chevron-right]
                         [icons/mux-chevron-down2])
                 :on-click #(let [new-state (not collapsed?)]
                              (reset! section-collapsed? new-state)
                              (grid-storage/set-section-collapsed entity-type section-key new-state))}]
               [:div.title section-label]
               (when (= system-section "general_info")
                 [:div.add-fields-container
                  [configure-fields-menu entity-type]])]

              (when-not collapsed?
                [:<>
                 [fields-view form-props section-fields]
                 (when @show-more?
                   [:div.toggle-expand
                    {:class (dom/classes-pairs-v :collapsed (not expanded?))}
                    [:hr {:class "toggle-expand-line"}]
                    [buttons/button
                     {:tertiary? true
                      :disabled? has-errors?
                      :text (if expanded?
                              (get-translation "entity.form.view-less" "View Less")
                              (get-translation "entity.form.view-more" "View More"))
                      :on-click (fn []
                                  (swap! user-expanded? not)
                                  (when (not @user-expanded?)
                                    (.scrollTo @panel-ref #js {:top 0 :behavior "smooth"})))}]])])]]]))})))

;; Special workaround for integrated fields in case we create internal entity
;; If it's an integrated field and entity is not integrated we want to show those fields in general section
;; Otherwise they should remain in integrated fields section
;; If we don't handle this, then we can have issue with Mandatory field that is configured as integrated
;; but not displayed on internal entity creation screen causing error when trying to save it
(defn handle-integration-fields
  [layout external-id]
  (let [[section-key integration-fields-section] (first (filter #(= (:system-section (val %)) "integration_fields") layout))
        general-key (ffirst (filter #(= (:system-section (val %)) "general_info") layout))]
    (if (nil? external-id)
      (-> layout
          (update-in [general-key :fields] #(apply conj % (:fields integration-fields-section)))
          (assoc-in [section-key :fields] []))
      layout)))

(defn left-side-panel [{:keys [active-tab]}]
  (fn [{:keys [additional-fields layout general-form-inner-tabs instance-section] :as form-props}]
    (let [entity-sections @(re-frame/subscribe [::subs/entity-sections (:entity-type form-props)])
          no-user-defined-sections? (nil? entity-sections)
          left-keys (->> layout
                         (filter (fn [[key _]]
                                   (and (string? key)
                                        (.startsWith key "0"))))
                         (map (fn [[key {:keys [system-section]}]]
                                {:key key
                                 :system-section system-section})))
          sorted-left-keys (sort-by :key left-keys)]

      [:section.left-side-panel
       (when instance-section
         [instance-section form-props])

       (doall
         (for [{:keys [key system-section]} sorted-left-keys]
           (with-meta
             (cond
               (= key "0-0") [top-left-side-section form-props key]
               (= system-section "integration_fields") (when additional-fields
                                                         [additional-fields form-props])
               :else [side-sections form-props key])
             {:key (str key)})))

       (when (and no-user-defined-sections?
                  additional-fields)
         [additional-fields form-props])

       (when general-form-inner-tabs
         [inner-tabs-panel form-props])])))

(defn right-side-panel [{:keys [layout new? entity-type] :as form-props}]
  (let [right-keys (->> layout
                        (filter (fn [[key _]]
                                  (and (string? key)
                                       (.startsWith key "1"))))
                        (map (fn [[key {:keys [system-section]}]]
                               {:key key
                                :system-section system-section})))
        sorted-right-keys (sort-by :key right-keys)
        test-case-versioning-enabled? @(re-frame/subscribe [::subs/test-case-versioning-enabled?])]

    [:section.right-side-panel
     {:class (dom/classes-pairs-v :instance-form (= entity-type :instance))}
     (doall
       (for [{:keys [key]} sorted-right-keys]
         (with-meta
           (cond
             (= key "1-0") (cond
                             (and (= entity-type :requirement)
                                  (not new?))
                             [testing-status-panel form-props]

                             (and (= entity-type :test)
                                  test-case-versioning-enabled?)
                             [test-versioning/test-case-versioning form-props]

                             (= entity-type :instance)
                             [:<>
                              [step-parameters/step-parameters-section form-props]

                              (when test-case-versioning-enabled?
                                [instance-version-section/instance-version-section form-props])]

                             :else nil)

             :else [side-sections form-props key])
           {:key (str key)})))]))

(defn entity-card
  [{:keys [form-ref] :as form-props}]
  [:div.entity-grid
   {:ref #(reset! form-ref %)}
   [link-to-external/link-to-external-modal nil
    (link-to-external/link-to-external-modal-options {:current-entity (:entity-type form-props)
                                                      :form-props form-props})]
   [left-side-panel form-props]
   [right-side-panel form-props]])

(defn general-entity-tab []
  {:tab-id :general
   :tab-title (get-translation "entity.tab.general" "General")
   :tab-view entity-card})

(defn- dirty-form-warning
  [form-props]
  (let [listener-fn (fn [props e]
                      (let [{:keys [entity-type form-id dirty-fields]} props
                            s (get-translation "testset.confirm.unsaved-changes"
                                               "You have unsaved changes, are you sure want to leave this page ?")
                            dirty? (boolean (seq @dirty-fields))
                            ;; TODO: should we have different confirmation if it's about attachments?
                            has-unsaved-attachments? @(re-frame/subscribe [::attachments/unsaved-attachments? entity-type])]
                        (when (or dirty? has-unsaved-attachments?)
                          ;; Text is ignored by some browsers (but some will display it)
                          (set! (.-returnValue e) s))))

        listener-id (atom nil)

        register-form! (fn [this]
                         (let [{:keys [form-id dirty-fields]} (reagent/props this)]
                           (router/add-dirty-fields! form-id dirty-fields)))]
    (reagent/create-class
      {:reagent-render (fn []
                         [:span])
       :component-did-mount (fn [this]
                              (reset! listener-id
                                      (.addEventListener js/window "beforeunload" (partial listener-fn (reagent/props this))))
                              (register-form! this))
       ;; Just to support changing subscriptions on hot reloads
       :component-did-update (fn [this]
                               (register-form! this))
       :component-will-unmount (fn [this]
                                 ;; Listener is dynamic now, so need to store it somewhere
                                 (when-let [id @listener-id]
                                   (.removeEventListener js/window "beforeunload" id))
                                 (router/remove-dirty-fields! (:form-id (reagent/props this))))})))

(def fetch-similar-debounced
  (gfuncs/debounce
    (fn [entity-type form-id]
      (re-frame/dispatch [::similar-items/fetch-similar-items entity-type form-id]))
    500))

(defn- set-field-prop-wrapper
  [form-id field-id field-state]
  (re-frame/dispatch-sync [::forms/set-field-state form-id field-id field-state])
  ;; TODO: something more flexible ?
  ;; Similar issues support
  (let [entity-type (cond
                      (= form-id :issue-main) :issue
                      (= form-id :test-main) :test
                      :else nil)
        has-similar-items? (and entity-type
                                (get-in metadata/entities-metadata
                                        [entity-type :has-similar-items?]))]
    (when (and has-similar-items?
               (or (= field-id :description)
                   (= field-id :name)))
      (fetch-similar-debounced entity-type form-id))))

(defn- make-relative-download-url
  [project file]
  (str "/projects/" (:id project) "/pattachments/" (:id file)))

(defn- make-markdown-preview-url
  [base-url]
  (str "![ ](" (str base-url "?style=thumb") ")"))

(defn format-error-message [error-message]
  (-> (str/split error-message #"\n")
      first
      (str/replace #"^File\s" "")))

;;handle uploaded attachments - insert specially formatted link into corresponding markdown field
(re-frame/reg-event-fx
  ::uploaded-attachment
  (fn [{db :db} [_ file upload-id
                 {:keys [field-info entity-type entity-id new? form-id] :as upload-options}
                 result]]
    (let [current-project (:current-project db)
          new-text (when field-info
                     (reduce
                       (fn [_ file]
                         (when (str/includes? (:file_type file) "image")
                           (markdown/insert-text! (:textarea-ref field-info)
                                                  (str
                                                    "\n"
                                                    (->> file
                                                         (make-relative-download-url current-project)
                                                         (make-markdown-preview-url))
                                                    "\n"))))
                       nil
                       (:data result)))
          formatted-error (format-error-message (:message result))]
      {:dispatch-n [(if (= "ok" (:result result))
                      [::notifications/success2 "Attachments uploaded" {:auto-close 1000}]
                      [::notifications/error2 formatted-error {:auto-close 1000}])
                    (when new-text
                      [::forms/set-field-value-internal form-id (:field-id field-info) new-text])
                    [::events/fetch-history]]})))

;; Slightly different response format and logic
(re-frame/reg-event-fx
  ::uploaded-stage-attachment
  (fn [{db :db} [_ file upload-id
                 {:keys [field-info entity-type entity-id new? form-id] :as upload-options}
                 stage-data]]
    (let [new-text (when field-info
                     (when (str/includes? (:type stage-data) "image")
                       (markdown/insert-text! (:textarea-ref field-info)
                                              (str "\n"
                                                   (make-markdown-preview-url (:url stage-data))
                                                   "\n"))))]
      {:dispatch-n [[::notifications/success2 "Attachment uploaded" {:auto-close 1000}]
                    (when new-text
                      [::forms/set-field-value-internal form-id (:field-id field-info) new-text])]})))

(re-frame/reg-sub
  :conflict-modal
  (fn [db _]
    (:conflict-modal db)))

;; used in memo fields (and description)
(re-frame/reg-event-fx
  ::upload-attachments
  (fn [{db :db :as cofx} [_ entity-type form-id entity-id field-info files]]
    (let [file (first files)
          new? (nil? entity-id)
          upload-options {:entity-type               entity-type
                          :entity-id                 entity-id
                          :form-id                   form-id
                          :field-info                field-info
                          :new?                      new?
                          :uploaded-attachment-event (if new?
                                                       [::uploaded-stage-attachment]
                                                       [::uploaded-attachment])}]
      (if new?
        {:dispatch [::attachments/stage-upload file upload-options]}
        {:dispatch [::attachments/upload file upload-options]}))))

(defn entity-form [_]
  (let [form-ref (atom nil)
        main-ref (atom nil)
        main-state (reagent/atom {})

        prepare-form-props (fn [{:keys [new?
                                        entity-type
                                        form-id
                                        general-form-inner-tabs
                                        dirty-fields]
                                 :as initial-form-props}]
                             (let [all-fields @(re-frame/subscribe [::subs/fields])
                                   entity-fields @(re-frame/subscribe [::subs/entity-fields entity-type])
                                   edit? (not new?)
                                   base-entity @(re-frame/subscribe [::forms/base-entity entity-type])
                                   layout (forms/build-entity-form-layout form-id new? all-fields entity-fields base-entity)
                                   remote-entity @(re-frame/subscribe [::forms/remote-entity entity-type])
                                   entity-id (:id base-entity)
                                   on-cancel-default (partial-dispatch [::forms/reset-form-state form-id new? entity-type])
                                   dirty-fields (or dirty-fields
                                                    ;; Default dirty-fields subscription
                                                    (re-frame/subscribe [::forms/dirty-fields form-id]))
                                   touched? @(re-frame/subscribe [::forms/modified-by-others? form-id])
                                   expanded? @(re-frame/subscribe [::grid.subs/any-expanded? entity-type])]
                               (assoc initial-form-props
                                      :edit? edit?
                                      :form-ref form-ref
                                      :main-ref main-ref
                                      :entity-id entity-id
                                      :entity-fields entity-fields
                                      :all-fields all-fields
                                      :dirty-fields dirty-fields
                                      :get-form-state (partial-subscribe [::forms/form-state form-id])
                                      :set-form-state (partial-dispatch [::forms/set-form-state form-id])
                                      :expanded? expanded?

                                      :get-field-props (partial-subscribe [::forms/field-props entity-type])
                                      :get-field-state (partial-subscribe [::forms/field-state form-id])
                                      :set-field-state (reagent/partial set-field-prop-wrapper form-id)

                                      :clear-linked-lists (partial-dispatch [::forms/clear-linked-lists form-id])
                                      :on-cancel (or (:on-cancel initial-form-props)
                                                     on-cancel-default)

                                      :on-files-attached (partial-dispatch [::upload-attachments entity-type form-id entity-id])

                                      :base-entity base-entity
                                      :remote-entity remote-entity
                                      :layout layout
                                      :touched?     touched?
                                      :mixpanel-attrs {"Entity" (mixpanel/entity-type->mixpanel entity-type)
                                                       "Source" "UI"}
                                      :general-form-inner-tabs (get general-form-inner-tabs (if new? :new :edit))
                                      :main-state main-state)))]
    (fn [initial-form-props]
      (let [{:keys [new?
                    entity-id
                    entity-type
                    form-id
                    header-tabs
                    default-tab
                    state-key
                    data-entity-type

                    entity-fields
                    entity-fields-optional?

                    base-entity
                    expanded?]
             :as form-props} (prepare-form-props initial-form-props)
            tab-ids (set (map :tab-id header-tabs))
            restored-tab  (header/active-tab-from-fragment)
            form-active-tab (or (:form-active-tab @main-state) restored-tab)
            active-tab (or (when (contains? tab-ids form-active-tab)
                             form-active-tab)
                           default-tab
                           ;; General would be default default tab
                           :general)
            state-key (or state-key
                          (subs/index-grid-keys entity-type))
            data-entity-type (or data-entity-type entity-type)
            data @(re-frame/subscribe [::grid.subs/data [data-entity-type state-key]])
            total-count @(re-frame/subscribe [::grid.subs/entities-total-count data-entity-type state-key])
            project-id (:id @(re-frame/subscribe [::subs/current-project]))
            table-id (get-in metadata/entities-metadata [data-entity-type :table-id])
            session-key (str project-id "-" table-id)
            current-page (or (grid-storage/load-current-page session-key) 1)
            per-page (or (grid-storage/load-per-page session-key) 10)
            full-data? @(re-frame/subscribe [::grid.subs/entities-full-data? data-entity-type state-key])
            local-state-updating
            (reagent/atom
              {:sort-settings     (grid-session-storage/load-sort-order session-key)
               :autofilters-state (grid-session-storage/load-autofilters session-key)})
            entity-columns (common-entities/get-entity-columns data-entity-type state-key)
            columns (grid2/process-columns local-state-updating {} entity-columns {})
            entities (grid2/process-rows data local-state-updating columns)
            pagination-props {:entities (if full-data? entities data)
                              :total-count (if full-data? (count entities) total-count)
                              :current-page current-page
                              :per-page per-page
                              :full-data? full-data?}
            conflict-modal @(re-frame/subscribe [:conflict-modal])]
        [:<>
         [dirty-form-warning form-props]

         (when (= entity-type :test)
           [test-versioning/label-reset-warning-modal])

         (when (and (or entity-fields-optional?
                        entity-fields)
                    ;; For new forms only fields configuration is required
                    (or new?
                    ;; For edit form we need entity data as well
                        base-entity))
           [:main#new-form
            {:ref (dom/ref-handler main-ref)}
            [keyboard-shortcuts/enable-scope {:shortcuts-type :entity-form}]
            (when-not expanded?
              [header/form-header form-props active-tab pagination-props])
            (when conflict-modal
              [conflict-resolution-modal
               {:conflicts (:conflicts conflict-modal)
                :on-cancel #(re-frame/dispatch [:close-conflict-modal])
                :form-id form-id
                :entity-type entity-type
                :entity-id entity-id}])

            [:div.tab-content
             {:class (str (name active-tab) "-tab" (when expanded? " expanded"))}
             (if-let [tab-view (:tab-view (first (filter #(= active-tab (:tab-id %)) header-tabs)))]
               [tab-view form-props]
               [:h4 "Tab id not found" (str active-tab)])]

            [footer form-props]])]))))

;; History Tab
(defn history-modal [{:keys [show?
                             hide!
                             row
                             users]}]
  [modal/bootstrap-modal
   {:class "history-modal"
    :is-open? show?
    :on-request-close hide!
    :close-button? true
    :centered? true}
   nil
   [:<>
    [:div.header
     "History Log"
     [:span.close-button
      {:on-click hide!}
      [icons/mux-x]]]

    [:div.history-row
     (let [user (users/find-user users (:user_id row))
           smartfox? (boolean (get-in row [:meta :smartfox_used]))]
       [:span.field
        [:span.field-name
         "User: "]
        [:span.field-value
         (fu/format-smartfox-suffix (:name user) smartfox?)]])
     [:span.field
      [:span.field-name
       "Date: "]
      [:span.field-value
       (fu/formatted-date-time (:created_at row))]]]

    [:div.history-row
     [:span.field
      [:span.field-name
       "Log:"]]]

    [:div.log-container
     (:log row)]]
   nil])

(defn history-page [_]
  (let [local-state (reagent/atom {})]
    (fn [{:keys [form-id
                 current-project
                 entity-type]
          :as form-props}]
      (let [table-id (str (:id current-project) "-" form-id "-history-grid") 
            modal-row (:selected-row @local-state)
            show-modal? (:show-modal? @local-state)]
        [:div.instances-container
         [history-modal
          {:show? show-modal?
           :hide! #(swap! local-state assoc :show-modal? false)
           :row modal-row
           :users @(re-frame/subscribe [::subs/users])}]

         (let [history-filter @(re-frame/subscribe [::subs/history-filter])]
           ^{:key (str "history-grid-" (hash history-filter))}
           [grid2/grid (cond-> {:columns @(re-frame/subscribe [::grid.subs/columns [entity-type :history2]])
                                :fields @(re-frame/subscribe [::subs/fields])
                                :data @(re-frame/subscribe [::grid.subs/history [entity-type]])
                                :class "data-grid-history"
                                :full-data? true
                                :resizable? false
                                :columns-widths-save (reagent/partial grid-storage/save-column-sizes table-id)
                                :columns-widths-load (reagent/partial grid-storage/load-column-sizes table-id)

                                :on-row-click (fn [e row]
                                                (swap! local-state assoc
                                                       :selected-row row
                                                       :show-modal? true))

                                :show-edit-columns? false

                                ;; Expand/collapse functionality
                                :on-expand-click (partial-dispatch [::common-events/toggle-expanded entity-type :history2])
                                :expanded? @(re-frame/subscribe [::grid.subs/expanded? entity-type :history2])

                                ;; Required for users filter to work correctly
                                :get-field-props (fn [field]
                                                   ;; TODO: fix this, it's really ugly
                                                   (re-frame/subscribe [::forms/field-props :instance
                                                                        (if (= :user_id field)
                                                                          :author
                                                                          field)]))}
                         history-filter
                         (assoc :autofilters-load (fn [] history-filter)))])]))))

(defn history-tab []
  {:tab-id :history
   :tab-title
   [:span.text-nowrap
    (get-translation "entity.tab.history" "History")]
   :tab-view history-page})
